<template>
  <soha-page-loading v-if="loading" />
  <div
    class="card card-custom gutter-b example example-compact p-5"
    v-else-if="!access"
  >
    <soha-no-access :message="$t(`${I18nPrefix}ACCESS_MESSAGE`)" />
  </div>
  <div v-else>
    <div class="soha-answer-test-header">
      <div class="w-100 d-flex py-3">
        <h3 class="soha-answer-title px-2 m-0">
          {{ $t(`${I18nPrefix}TEST_NAME`) }}: {{ title }}
        </h3>
      </div>
      <div class="row m-0">
        <p class="px-8 text-justify">
          {{ limitedDescription }}
          <label
            v-if="
              limitedDescription.length === description.length && formatTime
            "
          >
            {{ $t(`${I18nPrefix}ANSWER_TIME`) }}:
            <span class="font-weight-bolder">
              {{ formatTime }}
            </span>
          </label>
          <span
            class="cursor-pointer"
            style="color: var(--secondary-color)"
            v-if="shouldShowButton"
            @click="showFullDescription = !showFullDescription"
          >
            {{
              showFullDescription
                ? $t(`${I18nPrefix}CLOSE`)
                : $t(`${I18nPrefix}SHOW_MORE`)
            }}
          </span>
        </p>
      </div>
      <div class="row mx-0 my-4">
        <div class="col-12 col-sm-6 mb-4" v-if="testDate">
          <soha-filed :title="$t(`${I18nPrefix}SEND_TIME`)" :value="testDate" />
        </div>
        <div class="col-12 col-sm-6 mb-4">
          <soha-filed
            :title="$t(`${I18nPrefix}SCORE`)"
            :value="
              sumScores
                ? toPersianDigits(sumScores)
                : $t(`${I18nPrefix}NO_GRADING`)
            "
          />
        </div>
      </div>
    </div>
    <div class="p-5">
      <h4 class="soha-answer-title mb-8" v-if="optionalList.length > 0">
        {{ $t(`${I18nPrefix}MULTIPLE_CHOICE_QUESTIONS`) }}
      </h4>
      <div
        class="soha-question-box"
        v-for="(question, index) in optionalList"
        :key="index"
      >
        <h5>
          <span
            v-html="question.title"
            class="d-inline-block ql-editor w-100"
          />
        </h5>
        <div v-for="(answer, answerIndex) in question.items" :key="answerIndex">
          <div class="soha-question-item row">
            <div class="col-sm-11 col-10">
              <soha-checkbox
                checkbox-id="checkbox"
                v-if="question.type == 1 && answer.selectedItem"
                :label="
                  answer.title +
                  '  ' +
                  $t(`${I18nPrefix}SCORE`) +
                  toPersianDigits(answer.score)
                "
                v-model="answer.selectedItem.customQuestionItemId"
                :true-value="answer.id"
                :false-value="null"
                :disable="true"
              />
              <soha-checkbox
                checkbox-id="checkbox"
                v-else-if="question.type == 1"
                :label="answer.title"
                :disable="true"
              />
              <soha-radio-button
                v-if="question.type == 2"
                :checked="!!answer.selectedItem"
                :disable="true"
                :text="answer.title"
              />
            </div>
            <span
              class="col-sm-1 col-2 text-right p-0"
              :style="!answer.selectedItem ? 'color: var(--muted-color)' : null"
            >
              {{ toPersianDigits(answer.score) }}
              {{ "  " }}
              {{ $t(`${I18nPrefix}SCORE`) }}
            </span>
          </div>
        </div>
      </div>

      <h4 class="soha-answer-title my-8" v-if="correctionList.length > 0">
        {{ $t(`${I18nPrefix}CORRECTION_QUESTIONS`) }}
      </h4>
      <div
        class="soha-question-box row"
        v-for="(question, index) in correctionList"
        :key="question.id"
      >
        <h5 class="col-sm-11 col-10">
          <span
            v-html="question.title"
            class="d-inline-block pl-2 ql-editor w-100"
          />
        </h5>
        <span class="col-sm-1 col-2 text-right p-0">
          {{ toPersianDigits(question.score) }}
          {{ "  " }}
          {{ $t(`${I18nPrefix}SCORE`) }}
        </span>
        <div class="col-12">
          <p class="text-justify" v-if="question.answer">
            {{ question.answer.answer }}
          </p>
          <soha-no-data
            width="100"
            height="100"
            v-else
            :message="$t(`${I18nPrefix}NO_ANSWER`)"
          />
        </div>
        <div class="col-12">
          <soha-text-field
            :disabled="!accessCorrectionTest || !question.answer"
            type="number"
            v-model="correctionScore[index].employerScore"
            :error="correctionScore[index].error"
            :placeholder="$t(`${I18nPrefix}SCORE_FIELD_PLACEHOLDER`)"
            class="w-100"
          />
        </div>
      </div>
    </div>
    <div class="p-5">
      <div class="d-flex justify-content-end">
        <soha-button
          @click="insertTestCorrection"
          v-if="accessCorrectionTest && correctionList.length"
          :loading="btnLoading"
          :text="$t(`${I18nPrefix}CORRECTION_TEST`)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import UIMixin from "@/mixins/UIMixin";
import ApiService from "@/core/services/api.service";
import { mapGetters } from "vuex";
import ValidationMixin from "@/mixins/ValidationMixin";
import GeneralMixin from "@/mixins/GeneralMixin";

const text_question_type = 3;

export default {
  name: "CustomTestAnswer",

  mixins: [UIMixin, ValidationMixin, GeneralMixin],

  props: {
    testInfo: {
      default: null,
    },
  },

  data() {
    return {
      I18nPrefix: "PAGES.CUSTOM_TEST.ANSWER.",
      access: true,
      loading: false,
      answerList: [],
      correctionList: [],
      optionalList: [],
      correctionScore: [],
      title: null,
      description: "",
      btnLoading: false,
      accessCorrectionTest: false,
      time: null,
      showFullDescription: false,
      answerDate: null,
    };
  },

  mounted() {
    this.getAnswer(true);
    this.accessCorrectionTest = this.canDo.includes("correctionTest");
  },

  methods: {
    getAnswer(loading) {
      this.loading = loading;
      ApiService.post("api/employer/customTests/showAnswer", {
        customTestId: this.testInfo.testId,
        resumeId: this.testInfo.resumeId,
        linkId: this.testInfo.linkId,
      }).then((response) => {
        if (!response.data.code) {
          this.answerList = response.data.questions;
          this.correctionList = [];
          this.optionalList = [];
          this.correctionScore = [];
          this.answerList.map((item) => {
            if (item.type == text_question_type) {
              this.correctionList.push(item);
              this.correctionScore.push({
                customTestQuestionId: item.id,
                employerScore: item.answer ? item.answer.employerScore : null,
                score: item.score,
                error: null,
                answer: item.answer,
              });
            } else {
              this.optionalList.push(item);
            }
            this.answerDate = item.answer?.created_at;
          });
          this.title = response.data.title;
          this.description = response.data.description ?? "";
          this.time = response.data.time;
        } else if (+response.data.code === 403) {
          this.access = false;
        } else {
          this.errorToast(response.data.message);
        }
        this.loading = false;
      });
    },

    insertTestCorrection() {
      if (!this.validateCorrectionScore()) return;
      this.btnLoading = true;
      ApiService.post("api/employer/customTests/testCorrection", {
        customTestId: this.testInfo.testId,
        resumeId: this.testInfo.resumeId,
        questions: this.correctionScore,
        linkId: this.testInfo.linkId,
        employerId: this.userId,
      }).then((response) => {
        if (+response.data.code === 225) {
          this.successToast(response.data.message);
          this.getAnswer();
        } else {
          this.errorToast(response.data.message);
        }
        this.btnLoading = false;
      });
    },

    validateCorrectionScore() {
      let valid = true;
      this.correctionScore.map((score) => {
        score.error = score.answer
          ? this.validateField(
              score.employerScore,
              this.$t(`${this.I18nPrefix}SCORE`)
            )
          : null;
        if (score.employerScore) score.error = this.validateMaximumScore(score);

        if (score.error) valid = false;
      });
      return valid;
    },

    validateMaximumScore(score) {
      return +score.employerScore > +score.score
        ? this.$t(`${this.I18nPrefix}MAX_SCORE`) +
            " " +
            this.toPersianDigits(score.score)
        : null;
    },
  },

  computed: {
    ...mapGetters(["companyId", "canDo", "userId"]),

    sumScores() {
      let sum = 0;
      let score = true;
      this.correctionList.forEach((item) => {
        if (!item.answer) score = false;
        sum += item.answer ? +item.answer.employerScore : 0;
      });

      this.optionalList.forEach((item) => {
        item.items.forEach((child) => {
          if (child.selectedItem) {
            sum += +child.score;
          }
        });
      });
      if (!score) return score;
      return sum;
    },

    testDate() {
      if (this.answerDate) {
        let newDate = new Date(this.answerDate);
        return this.toPersianDigits(
          this.$moment(newDate).format("dddd DD jMMMM YYYY HH:mm")
        );
      }
      return null;
    },

    formatTime() {
      const duration = this.$moment.duration(this.time);
      const hours = Math.floor(duration.asHours());
      const minutes = duration.minutes();

      if (hours > 0) {
        return (
          `${this.toPersianDigits(hours)} ساعت` +
          (minutes > 0 ? ` و ${this.toPersianDigits(minutes)} دقیقه ` : "")
        );
      } else if (minutes > 0) {
        return `${this.toPersianDigits(minutes)} دقیقه`;
      } else return "";
    },

    limitedDescription() {
      if (this.description.length < 260) return this.description;
      return this.showFullDescription
        ? this.description
        : this.description.slice(0, 260) + "...";
    },

    shouldShowButton() {
      return this.description.length > 260;
    },
  },
};
</script>

<style scoped>
.soha-answer-title {
  color: var(--primary-color);
}

.soha-answer-test-header {
  border: 0.077rem solid var(--box-border-color);
  padding: 0.769rem;
  margin: 1.154rem;
  border-radius: 0.385rem;
}

.soha-question-box {
  box-shadow: 0 0 0.385rem 0 var(--primary-color);
  border-radius: 0.385rem;
  padding: 0.769rem;
  margin: 0.769rem 0;
}
.soha-question-item {
  border: 0.077rem solid var(--box-border-color);
  box-shadow: 0.231rem 0.231rem 0.385rem -0.231rem var(--primary-color);
  border-radius: 0.385rem;
  padding: 0.385rem;
  margin: 0.769rem 0;
}
.soha-question-item label {
  margin: 0 !important;
}
</style>

<style>
.soha-question-box img {
  max-width: 50rem !important;
}

.ql-editor {
  text-align: left;
}
.ql-editor .ql-align-right {
  text-align: right !important;
}

@media (max-width: 768px) {
  .soha-question-box img {
    max-width: 30.769rem !important;
  }
}
@media (max-width: 530px) {
  .soha-question-box img {
    max-width: 21.538rem !important;
  }
}
@media (max-width: 400px) {
  .soha-question-box img {
    max-width: 13.846rem !important;
  }
}
</style>
